/**
 * Production-Ready - MillageInfoBuilder
 * Basic millage rate inputs validation and summary functionality
 * Backend integration pending (see docs/planning/bond-calculator-app-plan.md)
 */

import type { MillageInputs } from '~/types';

export interface MillageInfoSummary {
  totalMillageDuration: number;
  millageRange: {
    min: number;
    max: number;
  };
  averageMillage: number;
  peakToMinimumRatio: number;
}

export class MillageInfoBuilder {
  /**
   * Validates input parameters
   */
  static validateInputs(inputs: MillageInputs): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (inputs.peakMillage <= 0) {
      errors.push('Peak millage must be greater than 0');
    }

    if (inputs.peakMillage > 20) {
      errors.push('Peak millage cannot exceed 20 mills');
    }

    if (inputs.peakMillageDuration <= 0) {
      errors.push('Peak millage duration must be greater than 0');
    }

    if (inputs.peakMillageDuration > 20) {
      errors.push('Peak millage duration cannot exceed 20 years');
    }

    if (inputs.minimumMillage <= 0) {
      errors.push('Minimum millage must be greater than 0');
    }

    if (inputs.minimumMillage > 20) {
      errors.push('Minimum millage cannot exceed 20 mills');
    }

    if (inputs.minimumMillageDuration <= 0) {
      errors.push('Minimum millage duration must be greater than 0');
    }

    if (inputs.minimumMillageDuration > 20) {
      errors.push('Minimum millage duration cannot exceed 20 years');
    }

    if (inputs.peakMillage <= inputs.minimumMillage) {
      errors.push('Peak millage must be greater than minimum millage');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  /**
   * Builds a summary of the millage inputs for display
   */
  static buildSummary(inputs: MillageInputs): MillageInfoSummary {
    const totalMillageDuration = inputs.peakMillageDuration + inputs.minimumMillageDuration;

    // Calculate weighted average millage
    const totalWeightedMillage =
      inputs.peakMillage * inputs.peakMillageDuration +
      inputs.minimumMillage * inputs.minimumMillageDuration;
    const averageMillage = totalWeightedMillage / totalMillageDuration;

    const peakToMinimumRatio = inputs.peakMillage / inputs.minimumMillage;

    return {
      totalMillageDuration,
      millageRange: {
        min: inputs.minimumMillage,
        max: inputs.peakMillage,
      },
      averageMillage,
      peakToMinimumRatio,
    };
  }
}
